/**
 * 将站内发品表单（SHEIN 布局 Editor）组装为发品请求：
 * - 兼容层：Tiktok/Temu 风格 bg.goods.add 字段
 * - 主路径：附带 ttsProduct（TTS Create Product 原生体，后端优先使用）
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { TIKTOK_MODE_SEMI, tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import { hideTiktokPublishField } from '@/tiktok-platform/utils/tiktokPublishUiMode';
import { findCategoryPathIds } from '@/tiktok-platform/utils/tiktokCategoryTree';
import {
  buildTiktokGoodsAddPayload,
} from '@/tiktok-platform/utils/buildTiktokGoodsAddPayload';
import {
  buildTiktokTtsCreateProductPayload,
} from '@/tiktok-platform/utils/buildTiktokTtsCreateProductPayload';
import {
  buildTiktokModelTryOnSizeOptions,
  isIllegalTiktokTryOnSizeName,
  resolveGoodsModelTryOnSize,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import {
  isBlankTiktokAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { toTiktokSubmitColorName } from '@/tiktok-platform/utils/tiktokColorNameMap';
import { isLikelyBrowserReachableThumb } from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { collectTiktokSkcPreviewImgUrls } from '@/tiktok-platform/utils/tiktokProductImage';
import { isEditorAttrVisible } from '@/tiktok-platform/utils/tiktokAttrVisibility';

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function toMoneyCents(yuan) {
  const n = Number(yuan);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** 提交平台必须用 Tiktok 上传返回的 URL；本地/IPE 地址平台会拒单 */
function imageUrlForPublish(image) {
  if (!image) return '';
  return String(image.tiktokUrl || '').trim();
}

/** 仅接受已转 Tiktok 的图址（避免 materialImgUrl 误存 ipe/本地地址） */
function isTiktokHostedImageUrl(url = '') {
  const u = String(url || '').trim();
  if (!u) return false;
  return /cdnfe\.com|kwcdn\.com|pinduoduo\.com|tiktok\.com|img\.kwcdn|fancyalgo/i.test(u);
}

function publishImageUrl(imageOrUrl) {
  if (!imageOrUrl) return '';
  if (typeof imageOrUrl === 'string') {
    return isTiktokHostedImageUrl(imageOrUrl) ? imageOrUrl.trim() : '';
  }
  const tiktok = imageUrlForPublish(imageOrUrl);
  if (tiktok) return tiktok;
  return '';
}

function imageUrlForListPreview(image) {
  if (!image) return '';
  const local = String(
    image.previewUrl
    || image.publicUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
  const tiktok = String(image.tiktokUrl || '').trim();
  if (local && isLikelyBrowserReachableThumb(local)) return local;
  if (tiktok && isLikelyBrowserReachableThumb(tiktok)) return tiktok;
  return local || tiktok || '';
}

function collectTiktokPropDefs(attrTpl) {
  const map = new Map();
  const rows = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
    ...(attrTpl?.skcSaleAttrs || []),
    ...(attrTpl?.skuSaleAttrs || []),
  ];
  const rawInfos = attrTpl?.raw?.data?.[0]?.attribute_infos
    || attrTpl?.raw?.attribute_infos
    || [];
  for (const attr of rows) {
    const id = String(attr?.attributeId || '').trim();
    if (!id || map.has(id)) continue;
    const raw = (Array.isArray(rawInfos) ? rawInfos : []).find(
      (r) => String(r?.attribute_id ?? r?.attributeId) === id,
    );
    map.set(id, {
      attr,
      tiktokProp: raw?.tiktok_prop || attr?.tiktok_prop || null,
      rawValues: raw?.attribute_value_info_list || [],
    });
  }
  return map;
}

function findRawAttrValue(rawValues, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id) return null;
  return (Array.isArray(rawValues) ? rawValues : []).find((v) => (
    String(v?.attribute_value_id ?? '') === id
    || String(v?.tiktok_vid ?? '') === id
    || String(v?.attributeValueId ?? '') === id
  )) || null;
}

function findEditorAttrValue(values, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id) return null;
  return (Array.isArray(values) ? values : []).find((v) => String(v?.id) === id) || null;
}

/** 提交平台须用 attrs.get 原始属性值名（如 尼龙Nylon），勿用本地精简展示名 */
function officialPropValue(rawVal, editorVal, fallback = '') {
  return String(
    rawVal?.attribute_value
    || rawVal?.attribute_value_name
    || rawVal?.attributeValue
    || editorVal?.name
    || editorVal?.nameCn
    || fallback
    || '',
  ).trim();
}

/**
 * productPropertyReqs.valueUnit：原样透传 attrs 模板单位（如 g/m²、%）。
 * 成分无单位时兜底 "%"；勿把包装公制 cm/g 误塞进货品属性。
 */
export function resolveProductPropertyValueUnit(rawUnit, { isComposition = false } = {}) {
  const unit = String(rawUnit || '').trim();
  if (unit) return unit;
  if (isComposition) return '%';
  return '';
}

function isHandInputProductAttr(editorAttr, tiktokProp) {
  const mode = Number(editorAttr?.mode);
  const controlType = Number(tiktokProp?.controlType);
  return mode === 0 || controlType === 0;
}

/**
 * 将表单单个属性值解析为 Tiktok productPropertyReqs 条目。
 * - 手输（克重等）：vid 必须为 0，数字不可当属性值 id（否则会报 属性值名称有误）
 * - 下拉/成分：vid + 平台原始 propValue 必须一致
 */
export function resolveProductPropertyEntry(entry, { editorAttr, def } = {}) {
  const tiktokProp = def?.tiktokProp || editorAttr?.tiktok_prop || null;
  const values = def?.attr?.values || editorAttr?.values || [];
  const rawValues = def?.rawValues || [];
  const handInput = isHandInputProductAttr(editorAttr || def?.attr, tiktokProp);

  if (entry && typeof entry === 'object' && ('valueId' in entry || 'percent' in entry || 'attributeValueId' in entry)) {
    const valueId = entry.valueId ?? entry.attributeValueId ?? entry.vid;
    const rawVal = findRawAttrValue(rawValues, valueId);
    const opt = findEditorAttrValue(values, valueId);
    const vid = toInt(rawVal?.tiktok_vid ?? valueId, 0);
    const propValue = officialPropValue(rawVal, opt, entry.name || entry.label || entry.propValue || '');
    const numberInputValue = entry.percent != null ? String(entry.percent) : '';
    if (!propValue && !vid) return null;
    return { vid, propValue, numberInputValue };
  }

  const text = String(entry ?? '').trim();
  if (!text) return null;

  // 手输属性（克重等）：vid=0，数值只放 propValue；valueUnit 用模板单位（如 g/m²）。
  // numberInputValue 仅成分比例(controlType=16)使用，克重填了会报「数值录入信息必须为空」
  if (handInput) {
    return {
      vid: 0,
      propValue: text.replace(/\s*(g\/m²|g\/m2|gsm|g\/㎡)\s*$/i, '').trim() || text,
      numberInputValue: '',
    };
  }

  // 仅当该 id 确实是本属性的可选值时，才当作 vid（避免「180」克重误当 vid）
  const rawById = findRawAttrValue(rawValues, text);
  const optById = findEditorAttrValue(values, text);
  if (rawById || optById) {
    const vid = toInt(rawById?.tiktok_vid ?? optById?.id ?? text, 0);
    const propValue = officialPropValue(rawById, optById, '');
    if (!propValue && !vid) return null;
    return { vid, propValue, numberInputValue: '' };
  }

  const optByName = (values || []).find((v) => (
    String(v?.name || '') === text
    || String(v?.nameCn || '') === text
    || String(v?.label || '') === text
  ));
  if (optByName) {
    const rawVal = findRawAttrValue(rawValues, optByName.id);
    return {
      vid: toInt(rawVal?.tiktok_vid ?? optByName.id, 0),
      propValue: officialPropValue(rawVal, optByName, text),
      numberInputValue: '',
    };
  }

  // 自定义文本：无平台 vid
  return { vid: 0, propValue: text, numberInputValue: '' };
}

export function buildProductPropertyReqs(form, attrTpl) {
  const defs = collectTiktokPropDefs(attrTpl);
  const out = [];
  const attrs = form.productAttributes && typeof form.productAttributes === 'object'
    ? form.productAttributes
    : {};
  const pool = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
  ];
  // 不可见属性（如无里衬下的成分）勿提交，避免平台 6000002 父子关系报错
  Object.entries(attrs).forEach(([attributeId, value]) => {
    if (value == null || value === '') return;
    const editorAttr = pool.find((a) => String(a?.attributeId) === String(attributeId));
    if (editorAttr && !isEditorAttrVisible(editorAttr, attrs, pool)) return;
    const def = defs.get(String(attributeId)) || {
      attr: editorAttr,
      tiktokProp: editorAttr?.tiktok_prop || null,
      rawValues: [],
    };
    const tiktokProp = def?.tiktokProp || editorAttr?.tiktok_prop || null;
    if (tiktokProp?.isSale) return; // 销售属性走 spec
    const values = Array.isArray(value) ? value : [value];
    values.forEach((entry) => {
      const resolved = resolveProductPropertyEntry(entry, { editorAttr: editorAttr || def?.attr, def });
      if (!resolved) return;
      const { vid, propValue, numberInputValue } = resolved;
      if (!propValue && !vid) return;
      const isComposition = Boolean(
        editorAttr?.isComposition
        || Number(editorAttr?.mode) === 4
        || Number(tiktokProp?.controlType) === 16
        || numberInputValue,
      );
      out.push({
        templatePid: toInt(tiktokProp?.templatePid ?? attributeId),
        pid: toInt(tiktokProp?.pid ?? attributeId),
        refPid: toInt(tiktokProp?.refPid),
        propName: String(tiktokProp?.name || def?.attr?.name || editorAttr?.name || ''),
        vid,
        propValue,
        valueUnit: resolveProductPropertyValueUnit(tiktokProp?.valueUnit, { isComposition }),
        numberInputValue,
        controlType: tiktokProp?.controlType,
      });
    });
  });
  return out;
}

function buildSpecPropertyReqs(form, attrTpl) {
  const out = [];
  const colorAttr = attrTpl?.skcSaleAttrs?.[0];
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0];
  const defs = collectTiktokPropDefs(attrTpl);

  const pushSpec = (attrDef, valueId, label) => {
    if (!attrDef) return;
    const id = String(attrDef.attributeId);
    const def = defs.get(id);
    const tiktokProp = def?.tiktokProp;
    const rawVal = (def?.rawValues || []).find((v) => String(v.attribute_value_id) === String(valueId));
    out.push({
      templatePid: toInt(tiktokProp?.templatePid ?? id),
      pid: toInt(tiktokProp?.pid ?? id),
      refPid: toInt(tiktokProp?.refPid),
      propName: String(tiktokProp?.name || attrDef.name || ''),
      vid: toInt(rawVal?.tiktok_vid ?? valueId, 0),
      propValue: String(label || rawVal?.attribute_value || ''),
      valueUnit: String(tiktokProp?.valueUnit || ''),
      specId: toInt(rawVal?.tiktok_spec_id ?? valueId, 0),
      parentSpecId: toInt(tiktokProp?.parentSpecId, 0),
      valueGroupId: toInt(rawVal?.tiktok_value_group_id, 0),
      valueGroupName: String(rawVal?.tiktok_value_group_name || ''),
    });
  };

  (form.skcList || []).forEach((skc) => {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    if (!isBlankTiktokAttributeValueId(resolved.colorValueId)) {
      pushSpec(colorAttr, resolved.colorValueId, resolved.colorLabel);
    } else if (String(resolved.colorExtraValue || resolved.colorLabel || '').trim()) {
      // 自定义颜色：无平台值 ID，仅带 propValue（提交体走 TTS value_name）；
      // 站内默认中文色名，提交前统一转英文；未映射且含中文则拒绝
      const rawCustom = resolved.colorExtraValue || resolved.colorLabel;
      const enName = toTiktokSubmitColorName(rawCustom);
      if (!enName) {
        throw new Error(fixedUiTpl(
          '自定义颜色「{0}」须为英文（平台不接受中文）。常见色名会自动转英文，其余请改用英文填写',
          String(rawCustom).trim(),
        ));
      }
      pushSpec(colorAttr, '', enName);
    }
    (skc.skuRows || []).forEach((sku) => {
      if (!isBlankTiktokAttributeValueId(sku.sizeValueId)) {
        pushSpec(sizeAttr, sku.sizeValueId, sku.sizeLabel);
      } else if (String(sku.sizeExtraValue || sku.sizeLabel || '').trim()) {
        pushSpec(sizeAttr, '', sku.sizeExtraValue || sku.sizeLabel);
      }
    });
  });

  // 去重
  const seen = new Set();
  return out.filter((row) => {
    const key = `${row.templatePid}|${row.specId}|${row.vid}|${row.propValue}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectCarouselUrls(form) {
  const urls = [];
  const push = (img) => {
    const u = imageUrlForPublish(img);
    if (u && !urls.includes(u)) urls.push(u);
  };
  (form.skcList || []).forEach((skc) => {
    push(skc.mainImage);
    (skc.detailImages || []).forEach(push);
    Object.values(skc.showroomSlots || {}).forEach(push);
  });
  (form.carouselImages || []).forEach(push);
  return urls;
}

/** 素材图：优先手传槽位；未传时由 ensureTiktokMaterialFromMainOnForm 从主图裁切补齐 */
function resolveMaterialImgUrl(form) {
  const fromSlot = publishImageUrl(form.materialImage);
  if (fromSlot) return fromSlot;
  return publishImageUrl(String(form.materialImgUrl || '').trim());
}

/** 主图视频 → productCarouseVideoReqList（可选；有则须带平台 vid/videoUrl） */
function buildProductCarouseVideoReqList(form) {
  return (Array.isArray(form.tiktokVideos) ? form.tiktokVideos : [])
    .filter((v) => v && typeof v === 'object')
    .map((v) => {
      const vid = String(v.vid || '').trim();
      const videoUrl = String(v.videoUrl || '').trim();
      if (!vid && !videoUrl) return null;
      // 仅有 ipe 预览地址、尚未转平台时不送，避免空壳被拒
      if (!vid && !isTiktokHostedImageUrl(videoUrl) && !/http/i.test(videoUrl)) return null;
      return {
        vid,
        coverUrl: String(v.coverUrl || '').trim(),
        videoUrl,
        width: toInt(v.width, 0),
        height: toInt(v.height, 0),
      };
    })
    .filter(Boolean)
    .filter((v) => v.vid || v.videoUrl);
}

function resolveCatIds(form) {
  if (Array.isArray(form.catPathIds) && form.catPathIds.length) {
    return form.catPathIds.map((id) => toInt(id)).filter((id) => id > 0);
  }
  if (Array.isArray(form.categoryPathIds) && form.categoryPathIds.length) {
    return form.categoryPathIds.map((id) => toInt(id)).filter((id) => id > 0);
  }
  const fromTree = findCategoryPathIds(form.categoryTree || form.categories, form.categoryId);
  if (fromTree.length) return fromTree.map((id) => toInt(id)).filter((id) => id > 0);
  const leaf = toInt(form.categoryId);
  return leaf > 0 ? [leaf] : [];
}

function buildProductSkcReqs(form, attrTpl, opts) {
  const semiManaged = Boolean(opts.semiManaged);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0];
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0];
  const clothing = Boolean(form.clothingCategory) || Boolean(colorAttr);
  const carousel = collectCarouselUrls(form);
  const defs = collectTiktokPropDefs(attrTpl);

  const skcSource = (form.skcList || []).filter((skc) => (
    !isBlankTiktokAttributeValueId(skc?.colorValueId)
    || String(skc?.colorExtraValue || skc?.colorLabel || '').trim()
    || !clothing
  ));

  const list = (skcSource.length ? skcSource : [{}]).map((skc, skcIndex) => {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const skcPreviewUrls = clothing
      ? collectTiktokSkcPreviewImgUrls(skc, imageUrlForPublish)
      : [];
    const preview = skcPreviewUrls[0]
      || imageUrlForPublish(skc.mainImage)
      || carousel[0]
      || '';
    const colorBlockUrl = imageUrlForPublish(skc.colorBlockImage);
    let mainProductSkuSpecReqs;
    if (clothing && colorAttr && !isBlankTiktokAttributeValueId(resolved.colorValueId)) {
      const def = defs.get(String(colorAttr.attributeId));
      const rawVal = (def?.rawValues || []).find(
        (v) => String(v.attribute_value_id) === String(resolved.colorValueId),
      );
      const specId = toInt(
        resolved.tiktokSpecId ?? rawVal?.tiktok_spec_id ?? resolved.colorValueId,
        0,
      );
      if (!(specId > 0)) {
        throw new Error(fixedUiTpl(
          '颜色「{0}」缺少平台规格 ID，请重新选择或创建颜色规格',
          resolved.colorLabel || resolved.colorExtraValue || fixedUi('未命名'),
        ));
      }
      mainProductSkuSpecReqs = [{
        parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
        parentSpecName: String(def?.tiktokProp?.name || colorAttr.name || fixedUi("颜色")),
        specId,
        specName: String(resolved.colorLabel || rawVal?.attribute_value || ''),
      }];
    } else if (clothing && colorAttr) {
      // 平台未下发颜色枚举：按自定义颜色提交（TTS 走 value_name，specId 置 0）
      const def = defs.get(String(colorAttr.attributeId));
      const rawCustom = resolved.colorLabel || resolved.colorExtraValue;
      const enName = toTiktokSubmitColorName(rawCustom);
      if (String(rawCustom || '').trim() && !enName) {
        throw new Error(fixedUiTpl(
          '自定义颜色「{0}」须为英文（平台不接受中文）。常见色名会自动转英文，其余请改用英文填写',
          String(rawCustom).trim(),
        ));
      }
      mainProductSkuSpecReqs = [{
        parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
        parentSpecName: String(def?.tiktokProp?.name || colorAttr.name || fixedUi("颜色")),
        specId: 0,
        specName: enName,
      }];
    } else {
      mainProductSkuSpecReqs = [{ parentSpecId: 0, parentSpecName: '', specId: 0, specName: '' }];
    }

    const skuRows = (skc.skuRows || []).length
      ? skc.skuRows
      : [{ supplierSku: '' }];
    const skcExtCode = String(resolved.supplierCode || form.supplierCode || '').trim();

    const productSkuReqs = skuRows.map((sku, skuIndex) => {
      const priceYuan = sku.supplyPrice ?? sku.price ?? form.supplyPrice ?? form.defaultPriceYuan ?? 0;
      const stock = sku.stock ?? form.stockQty ?? form.defaultStock ?? 0;
      const specReqs = [];
      if (clothing && colorAttr && !isBlankTiktokAttributeValueId(resolved.colorValueId)) {
        const def = defs.get(String(colorAttr.attributeId));
        const rawVal = (def?.rawValues || []).find(
          (v) => String(v.attribute_value_id) === String(resolved.colorValueId),
        );
        specReqs.push({
          parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
          parentSpecName: String(def?.tiktokProp?.name || colorAttr.name || ''),
          specId: toInt(
            resolved.tiktokSpecId ?? rawVal?.tiktok_spec_id ?? resolved.colorValueId,
            0,
          ),
          specName: String(resolved.colorLabel || ''),
        });
      } else if (clothing && colorAttr) {
        // 自定义颜色：平台未下发枚举，specId 置 0，值走 specName（TTS 侧为 value_name，中文转英文）
        const def = defs.get(String(colorAttr.attributeId));
        const rawCustom = resolved.colorLabel || resolved.colorExtraValue;
        const enName = toTiktokSubmitColorName(rawCustom);
        if (String(rawCustom || '').trim() && !enName) {
          throw new Error(fixedUiTpl(
            '自定义颜色「{0}」须为英文（平台不接受中文）。常见色名会自动转英文，其余请改用英文填写',
            String(rawCustom).trim(),
          ));
        }
        specReqs.push({
          parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
          parentSpecName: String(def?.tiktokProp?.name || colorAttr.name || ''),
          specId: toInt(resolved.tiktokSpecId, 0),
          specName: enName,
        });
      }
      if (sizeAttr && !isBlankTiktokAttributeValueId(sku.sizeValueId)) {
        const def = defs.get(String(sizeAttr.attributeId));
        const rawVal = (def?.rawValues || []).find(
          (v) => String(v.attribute_value_id) === String(sku.sizeValueId),
        );
        specReqs.push({
          parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
          parentSpecName: String(def?.tiktokProp?.name || sizeAttr.name || ''),
          specId: toInt(rawVal?.tiktok_spec_id ?? sku.sizeValueId, 0),
          specName: String(sku.sizeLabel || rawVal?.attribute_value || ''),
        });
      } else if (sizeAttr && String(sku.sizeExtraValue || sku.sizeLabel || '').trim()) {
        // 自定义尺码：specId 置 0，值走 specName
        const def = defs.get(String(sizeAttr.attributeId));
        specReqs.push({
          parentSpecId: toInt(def?.tiktokProp?.parentSpecId, 0),
          parentSpecName: String(def?.tiktokProp?.name || sizeAttr.name || ''),
          specId: toInt(sku.tiktokSpecId, 0),
          specName: String(sku.sizeExtraValue || sku.sizeLabel || ''),
        });
      }

      const skcCarouselFirst = skcPreviewUrls[0] || preview;
      const base = {
        extCode: String(
          sku.supplierSku
          || (skcExtCode ? `${skcExtCode}-${skuIndex + 1}` : `SKU-${skcIndex + 1}-${skuIndex + 1}`),
        ),
        currencyType: String(sku.currency || form.currency || 'CNY'),
        // 服饰 2000015：sku 预览图必须等于 skc 轮播第一张；勿传色块图
        thumbUrl: clothing
          ? skcCarouselFirst
          : (imageUrlForPublish(sku.skuImage) || colorBlockUrl || preview),
        productSkuSpecReqs: specReqs,
        supplierPriceYuan: priceYuan,
        stock,
        lengthMm: toInt(Number(sku.length || form.pkgLength || 10) * 10),
        widthMm: toInt(Number(sku.width || form.pkgWidth || 10) * 10),
        heightMm: toInt(Number(sku.height || form.pkgHeight || 5) * 10),
        weightMg: toInt(Number(sku.weight || form.weightGrams || 100) * 1000),
        // 站内固定公制 cm/g，一并带上卖家中心同款 input*
        length: sku.length || form.pkgLength || '',
        width: sku.width || form.pkgWidth || '',
        height: sku.height || form.pkgHeight || '',
        weightUi: sku.weight || form.weightGrams || '',
        // 净重（g）→ productSkuNetContentReq；选填
        netWeight: sku.netWeight || form.netWeightGrams || '',
        skuClassification: sku.skuClassification || 1,
        numberOfPieces: sku.numberOfPieces ?? sku.quantity ?? 1,
        quantity: sku.quantity,
        quantityUnit: sku.quantityUnit || sku.pieceUnitCode || 1,
        pieceUnitCode: sku.pieceUnitCode || sku.quantityUnit || 1,
        totalContains: sku.totalContains,
        individuallyPacked: sku.individuallyPacked,
        isSensitive: sku.isSensitive != null ? sku.isSensitive : (form.isSensitive ?? 0),
        sensitiveList: Array.isArray(sku.sensitiveList) ? sku.sensitiveList : [],
        suggestedPrice: sku.suggestedPrice,
        suggestedPriceCurrencyType: sku.suggestedPriceCurrencyType || form.currency || '',
        accessories: Array.isArray(sku.accessories) ? sku.accessories : [],
      };
      return base;
    });

    return {
      extCode: skcExtCode || `SKC-${skcIndex + 1}`,
      // 兼容旧 bg.goods.add：服饰 SKC 轮播 = 主图 + 细节图；TTS Create Product 走 main_images（1–9）
      previewImgUrls: clothing
        ? (skcPreviewUrls.length ? skcPreviewUrls : (preview ? [preview] : []))
        : (preview ? [preview] : carousel.slice(0, 1)),
      mainProductSkuSpecReqs,
      productSkuReqs,
    };
  });

  // 非服饰：强制单 SKC
  if (!clothing && list.length > 1) {
    return [list[0]];
  }
  return list;
}

export function samplePublishPayloadCurrency(payload) {
  const sku = payload?.productSkcReqs?.[0]?.productSkuReqs?.[0];
  if (!sku) return '';
  if (sku.currencyType) return String(sku.currencyType);
  if (Array.isArray(sku.siteSupplierPrices) && sku.siteSupplierPrices.length) return 'CNY';
  if (sku.supplierPrice != null) return 'CNY';
  return '';
}

/**
 * @param {object} form Editor 表单
 * @param {object} options
 */
export function buildTiktokPublishPayload(form, {
  shopType = '',
  modeCaps = null,
  siteList = null,
  attrTpl = null,
  categories = null,
  categoryVersion = '',
  categoryRules = null,
  region = '',
  titleMaxLength = 0,
  saveMode = '',
} = {}) {
  const shopCaps = modeCaps || tiktokShopCapabilities(shopType || form?.shopType);
  const resolvedShopType = String(shopType || form?.shopType || '');
  const semiManaged = shopCaps?.mode === TIKTOK_MODE_SEMI
    || /半托/i.test(resolvedShopType);
  // 跨境店 = 全球卖家（TikTok 语义）：Create Product 价格需带 sale_price
  const globalSeller = /跨境/.test(resolvedShopType);
  const skipSiteWarehouseFreight = Boolean(shopCaps?.skipSiteWarehouseFreight);

  const catIds = resolveCatIds({
    ...form,
    categoryTree: categories || form.categoryTree,
    categories: categories || form.categories,
  });
  if (!catIds.length) {
    throw new Error(fixedUi('请先选择叶子类目'));
  }

  const productName = String(form.title || form.productName || '').trim();
  if (!productName) {
    throw new Error(fixedUi('请填写商品名称'));
  }
  // 英文名为 Temu 半托字段；TTS Create Product 仅需 title
  const titleEn = String(form.titleEn || form.englishTitle || '').trim();
  if (!titleEn && !hideTiktokPublishField('titleEn')) {
    throw new Error(fixedUi('请填写英文名称'));
  }

  const sizeIds = Array.isArray(form.tiktokSizeTemplateIds)
    ? form.tiktokSizeTemplateIds.map((id) => toInt(id)).filter((id) => id > 0)
    : [];

  const selectedSiteIds = skipSiteWarehouseFreight
    ? []
    : (Array.isArray(form.selectedSites) ? form.selectedSites : [])
      .map((id) => toInt(id))
      .filter((id) => id > 0);
  const siteIds = skipSiteWarehouseFreight
    ? []
    : (Array.isArray(form.tiktokSiteIds) ? form.tiktokSiteIds : [])
      .map((id) => toInt(id))
      .filter((id) => id > 0);
  const resolvedSiteIds = siteIds.length ? siteIds : selectedSiteIds;
  const siteId = skipSiteWarehouseFreight
    ? 0
    : toInt(
      form.tiktokSiteId
      || resolvedSiteIds[0]
      || form.siteId,
    );

  // 素材图为 Temu 半托字段；TTS Create Product 无 materialImgUrl
  const materialImgUrl = resolveMaterialImgUrl(form);
  if (!materialImgUrl && !hideTiktokPublishField('materialImage')) {
    throw new Error(fixedUi('请上传素材图，或确保主图已上传以便自动裁成 1:1 素材图'));
  }

  const productCarouseVideoReqList = buildProductCarouseVideoReqList(form);
  const rawVideos = Array.isArray(form.tiktokVideos) ? form.tiktokVideos : [];
  if (rawVideos.length && !productCarouseVideoReqList.length) {
    throw new Error(fixedUi('主图视频未完成 Tiktok 上传（缺少 vid/videoUrl），请重新上传后再发品'));
  }

  const originRegion = String(form.originRegion || form.originCountry || 'CN').trim().toUpperCase() || 'CN';
  const originRegion2Id = form.originRegion2Id || form.region2Id || '';

  const goodsModelsRaw = Array.isArray(form.goodsModels) ? form.goodsModels : [];
  const tryOnSizeOptions = buildTiktokModelTryOnSizeOptions({
    sizeAttr: attrTpl?.skuSaleAttrs?.[0] || null,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  });
  const goodsModels = goodsModelsRaw.map((model) => {
    if (!model || typeof model !== 'object') return model;
    const resolved = resolveGoodsModelTryOnSize(model.sizeSpecName, tryOnSizeOptions);
    if (resolved) {
      return {
        ...model,
        sizeSpecId: resolved.sizeSpecId,
        sizeSpecName: resolved.sizeSpecName,
        sizeValueId: resolved.sizeValueId,
      };
    }
    if (isIllegalTiktokTryOnSizeName(model.sizeSpecName)) {
      throw new Error(fixedUi('模特试穿尺码不可为均码，请从商品已选尺码中选择'));
    }
    return model;
  });

  const payload = buildTiktokGoodsAddPayload({
    catIds,
    productName,
    productPropertyReqs: buildProductPropertyReqs(form, attrTpl),
    productSpecPropertyReqs: buildSpecPropertyReqs(form, attrTpl),
    productSkcReqs: buildProductSkcReqs(form, attrTpl, { semiManaged }),
    carouselImageUrls: collectCarouselUrls(form),
    materialImgUrl,
    productCarouseVideoReqList,
    clothingCategory: Boolean(attrTpl?.skcSaleAttrs?.length),
    defaultPriceYuan: form.supplyPrice ?? form.defaultPriceYuan,
    defaultStock: form.stockQty ?? form.defaultStock,
    originRegion,
    originRegion2Id,
    isSensitive: form.isSensitive ?? 0,
    siteId: siteId || undefined,
    siteIds: resolvedSiteIds.length ? resolvedSiteIds : (siteId ? [siteId] : []),
    warehouseId: skipSiteWarehouseFreight ? '' : (form.warehouseId || ''),
    freightTemplateId: skipSiteWarehouseFreight
      ? ''
      : (form.freightTemplateId || form.tiktokFreightTemplateId || ''),
    shipmentLimitSecond: skipSiteWarehouseFreight
      ? undefined
      : (form.shipmentLimitSecond || 86400 * 2),
    sizeTemplateIds: sizeIds,
    showSizeTemplateIds: sizeIds,
    goodsModels,
    productI18nReqs: titleEn ? [{ language: 'en', productName: titleEn }] : [],
    guideFiles: Array.isArray(form.guideFiles) ? form.guideFiles : [],
    originCertFiles: Array.isArray(form.originCertFiles) ? form.originCertFiles : [],
    packageShape: form.packageShape || '',
    packageTypeOuter: form.packageTypeOuter || '',
    outerPackageImages: Array.isArray(form.outerPackageImages) ? form.outerPackageImages : [],
    productOuterPackageImageReqs: Array.isArray(form.productOuterPackageImageReqs)
      ? form.productOuterPackageImageReqs
      : [],
    description: form.description || '',
    brandCode: form.brandCode || form.brandId || '',
  }, { semiManaged });

  // 同时附带 TTS Create Product 原生体，后端优先取 ttsProduct
  const ttsProduct = buildTiktokTtsCreateProductPayload(form, {
    attrTpl,
    categories: categories || form.categoryTree || form.categories,
    colorAttr: attrTpl?.skcSaleAttrs?.[0] || null,
    sizeAttr: attrTpl?.skuSaleAttrs?.[0] || null,
    globalSeller,
    categoryVersion,
    categoryRules,
    region,
    titleMaxLength,
    saveMode,
  });
  return {
    ...payload,
    description: ttsProduct.description,
    brandCode: form.brandCode || form.brandId || '',
    ttsProduct,
  };
}

/** 兼容旧调用：编辑增量暂未对接 Tiktok 编辑接口 */
export function buildTiktokEditPayload(form, options = {}) {
  return buildTiktokPublishPayload(form, { ...options, isEdit: true });
}
